import { TCoreShippingFormInstance } from './CoreShippingForm';
import { TCoreBillingFormInstance } from './CoreBillingForm';
import { TPickupPointFormInstance } from './PickupPointForm';
import { getUrlParams } from 'widgets/toolbox/util';
import { TInputRadioInstance } from 'widgets/forms/InputRadio';
import { TShippingMethodFormInstance } from 'core/checkout/CoreShippingMethodForm';
import { TPickupContactFormInstance } from 'core/checkout/PickupContactForm';
import { TBasketModel, TOrderModel } from 'types/models/order';

/**
 * @description Checkout Shipping step logic
 *
 * @param ShippingStepMixin Base widget for extending
 */
function CoreShippingStepMixinClassCreator(ShippingStepMixin: import('widgets/checkout/ShippingStepMixin').TShippingStepMixin) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class ShippingStepMixin
     * @augments TCheckoutMgr
     * @subcategory checkout
     * @classdesc Represents shipping step component with the next features:
     * 1. Manage Shipping step state on the checkout
     * 2. Submit shipping form
     */
    class CoreShippingStepMixin extends ShippingStepMixin {
        isPickupShipping = false;

        prefs() {
            return {
                pickupPointFormID: 'dwfrm_pickupPoint',
                shippingMethodFormID: 'dwfrm_shippingMethod',
                pickupContactFormID: 'dwfrm_pickupContact',
                copyPhoneFromBillingForPickup: false,
                ...super.prefs()
            };
        }

        /**
         * @description Activate shipping step
         */
        shippingActivate() {
            const activateForms = [
                this.prefs().billingFormID,
                this.prefs().shippingFormID,
                this.prefs().shippingMethodFormID,
                this.prefs().pickupPointFormID
            ];

            activateForms.forEach(formID => {
                this.getByIdDeep(formID, (formComponent: TCoreShippingFormInstance) => {
                    formComponent.activate({
                        order: this.order,
                        customer: this.customer
                    });

                    const urlParams = <{ errorMessageOnStepActivate: string }>getUrlParams();

                    if (urlParams.errorMessageOnStepActivate) {
                        formComponent.setError(urlParams.errorMessageOnStepActivate.toString());

                        this.sendShippingStepErrorEvent();
                    }
                });
            });

            this.eventBus().emit('checkout.shipping.step.activated', this);
        }

        /**
         * @description Submit shipping form
         */
        shippingSubmit(): Promise<void> {
            const shippingFormComponent = this.getByIdDeep(
                this.isPickupShipping ? this.prefs().pickupPointFormID : this.prefs().shippingFormID,
                (widget: TCoreShippingFormInstance) => widget
            );
            const billingFormComponent = this.getById(this.prefs().billingFormID, (widget: TCoreBillingFormInstance) => widget);
            const shippingMethodFormComponent = this.getById(this.prefs().shippingMethodFormID, (widget: TShippingMethodFormInstance) => widget);
            const pickupContactFormComponent = this.getByIdDeep(this.prefs().pickupContactFormID, (widget: TPickupContactFormInstance) => widget);

            const forms = [shippingFormComponent, pickupContactFormComponent, shippingMethodFormComponent, billingFormComponent];

            const allValid = forms.every(form => {
                if (!form) {
                    return true;
                }

                const formErrorPlaceholder = form.ref('errorFeedback').length ? 'errorFeedback' : 'errorMessageLabel';

                if (form.isValid()) {
                    form.clearError(formErrorPlaceholder);

                    return true;
                }

                const formValidationError = form.prefs().formValidationError || '';

                form.showAddressForm();
                form.validate();

                if (formValidationError) {
                    form.setError(formValidationError, true, formErrorPlaceholder);
                }

                return false;
            });

            if (!allValid) {
                this.sendShippingStepErrorEvent();

                return Promise.resolve();
            }

            if (this.isPickupShipping) {
                this.copyPhoneFromBillingToPickup(billingFormComponent);
            }

            return forms
                .reduce(async (chain: Promise<void>, item) => {
                    return chain.then(() => {
                        if (!item) {
                            return Promise.resolve();
                        }

                        return item.submitAsync();
                    });
                }, Promise.resolve())
                .then(() => {
                    this.eventBus().emit('checkout.shipping.step.submitted', this);
                })
                .catch(() => {
                    this.sendShippingStepErrorEvent();
                });
        }

        /**
         * @description Copy phone and areaCode from billing form to pickup point form
         * @param billingForm - Billing form widget
         */
        private copyPhoneFromBillingToPickup(billingForm: TCoreBillingFormInstance | undefined) {
            if (!billingForm || !this.prefs().copyPhoneFromBillingForPickup) {
                return;
            }

            const billingFields = billingForm.getFormFieldsShort();
            const { phone, areaCode } = billingFields;

            if (phone) {
                this.getByIdDeep(this.prefs().pickupPointFormID, (pickupForm: TPickupPointFormInstance) => {
                    pickupForm.fillAddressFields({ phone, areaCode: areaCode || '' }, true);
                });
            }
        }

        /**
         * @description Emit shipping step error event
         */
        sendShippingStepErrorEvent() {
            this.eventBus().emit('checkout.shipping.step.error', this);
        }

        /**
         *
         * @description Shipping submission result handler
         * @param shippingForm - Shipping form widget
         * @param data - Submission response from server
         * @listens ShippingForm#submit
         */
        shippingSubmitted(shippingForm: TCoreShippingFormInstance, data: Record<string, unknown>) {
            if (data.order) {
                this.order = <TOrderModel>data.order;
            }

            this.isOrderEmailSubscribed = Boolean(data.isOrderEmailSubscribed);
            this.isOrderSmsSubscribed = Boolean(data.isOrderSmsSubscribed);
            this.isOrderKakaoSubscribed = Boolean(data.isOrderKakaoSubscribed);

            this.callIfExists('updateSummaryEv', shippingForm, data.order);
        }

        /**
         *
         * @description Shipping submission result handler
         * @param shippingMathodForm - Shipping Mathod form widget
         * @param data - Submission response from server
         * @listens ShippingMathodForm#submit
         */
        shippingMethodSubmitted(shippingMathodForm: TShippingMethodFormInstance, data: Record<string, unknown>) {
            if (data.order) {
                this.order = <TOrderModel>data.order;
            }

            this.callIfExists('updateSummaryEv', shippingMathodForm, data.order);
        }

        /**
         * @description Update shipping methods by shipping form changes
         * @param shippingFormWidget - child widget, that fired event
         * @listens ShippingForm#updateshippingform
         */
        updateShippingMethodsEv(shippingFormWidget: TShippingMethodFormInstance) {
            // Ignore delivery-form method refreshes while the pickup tab is active — e.g. on Edit a saved
            // address auto-fills and would reset `isPickupPoint` server-side, dropping the PUDO method.
            if (this.isPickupShipping) {
                return;
            }

            this.getById(this.prefs().shippingMethodFormID, (shippingMethodFormComponent: TShippingMethodFormInstance) =>
                shippingMethodFormComponent.updateShippingState(shippingFormWidget)
            );
        }

        /**
         * @description Select shipping method handler
         * @param shippingMethodFormComponent - Shipping method form widget
         * @param inputRadio - Shipping method`s input
         * @listens InputRadio#change
         * @emits ShippingForm#updateshippingform
         * @emits "checkout.update.shipping.method"
         */
        selectShippingMethodEv(shippingMethodFormComponent: TShippingMethodFormInstance, inputRadio: TInputRadioInstance) {
            this.getByIdDeep(this.prefs().shippingFormID, (shippingFormComponent: TCoreShippingFormInstance) =>
                shippingFormComponent.selectShippingMethod(inputRadio)
            );
        }

        /**
         * @description Update shipping state by shipping method form changes
         * @param shippingFormComponent - Shipping form widget
         * @param basket - Basket model
         * @listens ShippingMethodForm#updateshippingstate
         * @emits ShippingForm#updateshippingform
         */
        updateShippingStateEv(shippingFormComponent: TCoreShippingFormInstance, basket: TBasketModel) {
            this.getById(this.prefs().shippingMethodFormID, (shippingMethodFormComponent: TShippingMethodFormInstance) =>
                shippingMethodFormComponent.updateState(basket)
            );
        }
    }

    return CoreShippingStepMixin;
}

export default CoreShippingStepMixinClassCreator;
